import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useAuth } from "../../../../../auth/auth";
import { addUser, getRoles } from "../../../../../api/apiCall";
import AccessForbidden from "../../../../../components/Access/AccessForbidden";
import Loading from "../../../../../components/Loading/Loading";

type FormValues = {
    name: string;
    surname: string;
    pts: string;
    username: string;
    phoneNumber: string;
    email: string;
    postCode: string;
    password: string;
    roleId: any;
    location: any;
};

const CreateWorker = ({menuClicked, setMenuClicked}: any) => {
    const {state} = useAuth();
    const [buttonLoading, setButtonLoading] = useState(false);
    const [roles, setRoles] = useState<any>([]);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = useForm<FormValues>();

    const handleCreateWorker = async (data: any) => {
        setButtonLoading(true);
        const response = await addUser(state.user?.token, data);
        if (response?.status === 200) {
            setTimeout(() => {
                setButtonLoading(false);
                toast.success("Worker added successfully");
                reset({
                    name: "",
                    surname: "",
                    pts: "",
                    phoneNumber: "",
                    email: "",
                    postCode: "",
                    username: "",
                    roleId: "",
                    location: "",
                });
                setMenuClicked("ManageUsers");
            }, 500);
        } else {
            setButtonLoading(false);
            toast.error("Something went wrong!");
        }
    };

    const getAllRoles = async () => {
        const response = await getRoles(state.user?.token);
        if (response) {
            setRoles(response.data);
        } else {
            setIsAdmin(true);
        }
        setLoading(false);
    };

    useEffect(() => {
        getAllRoles();
    }, []);

    if (loading) {
        return <Loading />;
    }

    if (isAdmin) {
        return <AccessForbidden />;
    }

    return (
        <Box sx={{ margin: "auto", backgroundColor: "#f2f5fc", padding: 5 }}>
            <Box sx={{ maxWidth: 650, margin: "auto", backgroundColor: "#fff", padding: 5 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
                    Create New Account
                </Typography>
                <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }} />

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr" },
                        gap: 3,
                        mt: 3
                    }}>
                    <TextField
                        label="Name"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        {...register("name", { required: "Name is required" })}
                    />
                    <TextField
                        label="Surname"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={!!errors.surname}
                        helperText={errors.surname?.message}
                        {...register("surname", { required: "Surname is required" })}
                    />
                    <TextField
                        label="Username"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={!!errors.username}
                        helperText={errors.username?.message}
                        {...register("username", { required: "Username is required" })}
                    />
                    <TextField
                        label="PTS"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={!!errors.pts}
                        helperText={errors.pts?.message}
                        {...register("pts", { required: "PTS is required" })}
                    />
                    <TextField
                        label="Phone Number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber?.message}
                        {...register("phoneNumber", { required: "Phone number is required" })}
                    />
                    <TextField
                        label="Email"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                        {...register("email", {
                            required: "Email is required",
                            pattern: {
                                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                message: "Please enter a valid email address",
                            },
                        })}
                    />
                    <TextField
                        label="Post Code"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={!!errors.postCode}
                        helperText={errors.postCode?.message}
                        {...register("postCode", {
                            required: "Post code is required"
                        })}
                        onBlur={(e) => {
                            const value = e.target.value.toUpperCase();
                            setValue("postCode", value);
                        }}
                    />
                    <TextField
                        label="Location"
                        variant="outlined"
                        fullWidth
                        size="small"
                        {...register("location")}
                    />
                    <TextField
                        select
                        label="Role"
                        size="small"
                        fullWidth
                        error={!!errors.roleId}
                        {...register("roleId", { required: "Role is required" })}
                    >
                        {roles.map((role: any) => (
                            <MenuItem key={role.id} value={role.id}>
                                {role.roleName}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 3
                    }}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        style={{ boxShadow: "none", backgroundColor: "#156bc2", width: 200 }}
                        disabled={buttonLoading}
                        onClick={handleSubmit(handleCreateWorker)}
                    >
                        {buttonLoading ? (
                            <CircularProgress size={25} style={{ color: "white" }} />
                        ) : (
                            "Create User"
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default CreateWorker;